import { orderDetailsType, service } from "../state_management/actionTypes";
import { c_item, e_item, parseIdQuan } from "../state_management/epics/order";
import {
  Item,
  cartInitKeys,
  cartInitialState,
  cartItem,
  extrasCartItem,
  gift_value
} from "../state_management/reducers/cart";
import { public_items } from "./analytics";
import { calc_price_with_discount } from "./misc";

export function combineCart(
  items: cartInitialState,
  exclude: cartInitKeys[] = []
): Item[] {
  const { cart: c, extras: e } = items;
  const cart = exclude.includes("cart") ? [] : c;
  const extras = exclude.includes("extras") ? [] : e;
  return [...cart, ...extras];
}

export function getCartMainTotal(items: Item[]): number {
  let total = 0;
  for (const item of items) {
    total = total + item.quantity * item.item.price;
  }
  return total;
}

export const getCartTotal = (
  params:
    | {
        orderDirect: true;
        cart: orderDetailsType["data"]["services"];
        extras: orderDetailsType["data"]["extra_services"];
        gift_values: orderDetailsType["data"]["gift_values"];
        raw_cart?: never;
      }
    | {
        orderDirect?: never;
        cart?: never;
        extras?: never;
        gift_values?: never;
        raw_cart: cartInitialState;
      }
) => {
  const { orderDirect, cart, extras, gift_values, raw_cart } = params;
  let cartCombined: (cartItem | extrasCartItem)[];

  if (orderDirect) {
    const {
      cart: cart_items,
      extras: extra_items,
      gift_services
    } = parseToCartItem({ cart, extras, gift_values });
    cartCombined = [...cart_items, ...extra_items, ...gift_services];
  } else {
    cartCombined = [...raw_cart.cart, ...raw_cart.extras];
  }

  let total = 0;
  for (const item of cartCombined) {
    total = total + item.quantity * item.item.price_with_discount;
  }
  return total;
};

export function parseGACart(items: cartInitialState): public_items {
  const { cart, extras } = items;
  return [
    ...cart.map((item) => ({
      id: item.id,
      type: item.type,
      quantity: item.quantity,
      item: item.item,
      extra: false
    })),
    ...extras.map((item) => ({
      id: item.id,
      item: item.item,
      extra: true,
      type: item.type,
      quantity: item.quantity
    }))
  ];
}

export function split_cart(items: cartItem[]): {
  gift_values: cartItem[];
  cart: cartItem[];
} {
  const cart = items.filter((item) => item.type === "typical");
  const gift_values = items.filter((item) => item.type === "gift");

  return { cart, gift_values };
}

export const epics_cart_items = (
  details: {
    cart: c_item[];
    extras: e_item[];
    gift_values: (c_item & { item: { price: number } })[];
  },
  include: (keyof cartItem["item"])[] = []
): {
  services: {
    id: number;
    quantity: number;
    parent_id: undefined;
  }[];
  extra_services: {
    id: number;
    quantity: number;
    parent_id: number;
  }[];
  gift_values: gift_value[];
} => {
  const { cart, extras, gift_values } = details;
  const includeObj = (
    item: (typeof cart)[0] | (typeof extras)[0] | (typeof gift_values)[0]
  ) => {
    let obj: Partial<{ [key in (typeof include)[0]]: cartItem["item"][key] }> =
      {};
    include.forEach((i) => {
      if (i) {
        obj = {
          ...obj,
          [i]: item[i as keyof typeof item]
        };
      }
    });
    return obj;
  };

  return {
    services: cart.map((item) => ({
      ...parseIdQuan({ item, extra: false }),
      ...includeObj(item)
    })),
    extra_services: extras.map((item) => ({
      ...parseIdQuan({ item, extra: true }),
      ...includeObj(item)
    })),
    gift_values: gift_values.map((item) => ({
      ...parseIdQuan({ item, extra: false }),
      service_id: item.id,
      quantity: item.quantity,
      value: item.item.price,
      ...includeObj(item)
    }))
  };
};

export function cart_item_from_service(params: {
  service: service;
  giftValue?: { value: number; service_id: number };
  count: number;
}): cartItem | undefined {
  const { service, giftValue, count } = params;

  const item = (() => {
    switch (service.type) {
      case "gift": {
        if (!giftValue) {
          return null;
        }

        const gift_item: service = {
          ...service,
          discount: 0,
          price: giftValue?.value,
          price_with_discount: giftValue?.value,
          type: "gift"
        };

        return gift_item;
      }

      default: {
        return service;
      }
    }
  })();

  if (!item) {
    return;
  }

  const Item: cartItem = {
    id: service.id,
    item: {
      ...item,
      extra: false
    },
    quantity: count,
    parent_id: undefined,
    ...(service.type === "gift" ? { type: "gift" } : { type: "typical" })
  };

  return Item;
}

export const parseToCartItem = (params: {
  cart: orderDetailsType["data"]["services"];
  extras: orderDetailsType["data"]["extra_services"];
  gift_values: orderDetailsType["data"]["gift_values"];
}): cartInitialState & { gift_services: cartItem[] } => {
  const { cart, extras, gift_values } = params;

  const cart_items: cartInitialState["cart"] = cart.map((item) => ({
    id: item.id,
    item: {
      ...item,

      categories: [],
      category_id: 0,
      deposit: 0,
      description: "",
      extra: false,
      extra_services: [],
      gift_values: [],
      included_services: [],
      included_label: "",
      parent_id: undefined,
      position: 0,
      reviews_count: 0,
      type: "typical",
      extra_max_quantity: 0
    },
    parent_id: undefined,
    quantity: item.quantity,
    type: "typical"
  }));

  const extra_items: cartInitialState["extras"] = (extras || []).map(
    (item) => ({
      id: item.id,
      item: {
        ...item,

        extra: true,
        price_with_discount: calc_price_with_discount(
          item.discount || 0,
          item.price
        ),
        extra_max_quantity: 0
      },
      parent_id: item.parent_id,
      quantity: item.quantity,
      type: "extra"
    })
  );

  const gift_services: cartInitialState["cart"] = (gift_values || []).map(
    (T) => {
      const item = T.service;
      return {
        id: item.id,
        item: {
          ...item,
          price: T.value,
          discount: null,
          price_with_discount: T.value,
          description: item.description || "",
          extra: false,
          parent_id: undefined,
          included_label: "",
          category_id: 0
        },
        parent_id: undefined,
        quantity: T.quantity || 1,
        type: "gift"
      };
    }
  );

  return {
    cart: cart_items,
    extras: extra_items,
    gift_services
  };
};

export const only_gift = (cart: cartInitialState): boolean => {
  return ![...cart.cart, ...cart.extras].some((item) => {
    return item.type !== "gift";
  });
};
